import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Check } from '@fx-ui/fine-design';
import { platformIntl } from '../../../../../../constants';
import { MessageReceiver, MsgTargetSetDialog } from '../../../../components';
import { MessageConfigItem, MessageChannel, MessageReceiverMap } from '../../../../interfaces';
import { filter, forEach, isEmpty } from '@libs/shared';
import { IReceiverData } from '../../../message-receiver/MessageReceiver';
import { MessageSettingContext } from '../../../../context';

interface MsgConfigSectionProps {
    className?: string;
    /** 消息渠道配置勾选框相关属性 */
    checkProps: {
        /** 是否勾选 */
        checked?: boolean;
        /** 勾选文案 */
        text: string;
        /** 自定义后缀 */
        suffix?: React.ReactNode;
        /** 是否允许勾选切换 */
        disabled?: boolean;
        /** 勾选回调 */
        onCheck?: (value: boolean) => void;
    };
    /** 消息接收者配置相关属性 */
    receiverProps?: {
        /** 消息接收者 */
        receivers?: MessageConfigItem['target'];
        /** 是否允许编辑 */
        editable?: boolean;
        /** 接收者编辑后回调 */
        onReceiverChange?: (receiver: MessageConfigItem['target']) => void;
    };
    /** 消息接收渠道 */
    messageChannel: MessageChannel;
    /** 自定义的操作区 */
    customOperation?: React.ReactNode;
}

/**
 * 预设的消息接收对象
 */
const enum PresetReceiver {
    /** 企业创建者 */
    CorpOwner = 'corp_owner',
    /** 系统管理员 */
    SystemAdmin = 'system_admin'
}

/**
 * 单个消息渠道配置
 */
const MsgConfigSection: React.FC<MsgConfigSectionProps> = (props) => {
    const { className, messageChannel, checkProps, receiverProps, customOperation } = props;
    const { receivers = {}, editable = true, onReceiverChange } = receiverProps || {};

    const [targetType, setTargetType] = useState<MessageChannel>();
    const [msgTargetVisible, setMsgTargetVisible] = useState<boolean>(false);
    const { receiverMap, onReceiverMapChange } = useContext(MessageSettingContext);

    /**
     * 获取除了系统管理员和企业创建者以外的提醒对象
     *
     * @param value 所有提醒对象列表
     */
    const getAddedTargetValue = (value: string[]) => {
        return filter(value, (item) => {
            return ![PresetReceiver.CorpOwner, PresetReceiver.SystemAdmin].includes(
                item as PresetReceiver
            );
        });
    };

    /**
     * 提醒对象弹窗确认点击回调
     */
    const handleTargetChange = (val: string[], nameMap: MessageReceiverMap) => {
        setMsgTargetVisible(false);
        if (!targetType) {
            return;
        }
        onReceiverChange?.({ ...receivers, extra_receivers: getAddedTargetValue(val) });
        onReceiverMapChange(nameMap);
    };

    /**
     * 提醒对象tag删除回调
     */
    const handleReceiverChange = (value: string[], targetType: string) => {
        setTargetType(targetType as MessageChannel);
        onReceiverChange?.({ ...receivers, extra_receivers: getAddedTargetValue(value) });
    };

    const extraReceivers = useMemo(() => {
        if (receivers) {
            return receivers?.extra_receivers;
        }
        return [];
    }, [receivers]);

    /**
     * 获取推送对象列表
     */
    const messageReceivers = useMemo(() => {
        const result: IReceiverData[] = [];
        if (receivers) {
            const { has_corp_owner, has_system_admin, extra_receivers = [] } = receivers;
            if (has_corp_owner) {
                result.push({
                    text: platformIntl.get('msg.corp_owner'),
                    value: PresetReceiver.CorpOwner,
                    editable: false
                });
            }
            if (has_system_admin) {
                result.push({
                    text: platformIntl.get('msg.system_admin'),
                    value: PresetReceiver.SystemAdmin,
                    editable: false
                });
            }
            forEach(extra_receivers, (item) => {
                const target = receiverMap[item];
                if (!isEmpty(target) && target?.name) {
                    result.push({
                        value: item,
                        text: target.name,
                        email_info: target.email_info,
                        phone_info: target.phone_info,
                        editable: true
                    });
                }
            });
        }
        return result;
    }, [receivers, receiverMap]);

    const renderOperation = () => {
        if (customOperation) {
            return customOperation;
        }

        if (!checkProps.checked) {
            return null;
        }

        return (
            <MessageReceiver
                targetType={messageChannel}
                data={messageReceivers}
                onChange={handleReceiverChange}
                edit={editable}
            />
        );
    };

    return (
        <div className={classNames('fx-msg-config-section', className)}>
            <div className="modifier-wrapper">
                <div className="check-wrapper">
                    <Check
                        className="message-channel-check"
                        hasPadding={false}
                        value={messageChannel}
                        checked={checkProps.checked}
                        onChange={(val) => checkProps.onCheck?.(Boolean(val))}
                        tabFocus
                        disabled={checkProps.disabled}
                    >
                        {checkProps.text}
                    </Check>
                    {checkProps.suffix}
                </div>
                {editable && checkProps.checked && (
                    <Button
                        className="modify-btn"
                        compact
                        type="text"
                        onClick={() => {
                            setTargetType(messageChannel);
                            setMsgTargetVisible(true);
                        }}
                    >
                        {platformIntl.get('modify')}
                    </Button>
                )}
            </div>
            {renderOperation()}
            <MsgTargetSetDialog
                visible={msgTargetVisible}
                value={extraReceivers}
                type={targetType}
                onClose={() => {
                    setTargetType(undefined);
                    setMsgTargetVisible(false);
                }}
                onOk={handleTargetChange}
            />
        </div>
    );
};

export default MsgConfigSection;
